export const UnitType = Object.freeze({
  ITEM: 'item',
  DAY: 'day',
  NIGHT: 'night',
  HOUR: 'hour',
  INQUIRY: 'inquiry',
});

export const StatusCode = Object.freeze({
  SUCCESS: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
  TOO_MANY_REQUESTS: 429,
});

export const UserTypePreference = Object.freeze({
  BUYER: 'buyer',
  SELLER: 'seller',
});

export const ListingStatusSection = Object.freeze({
  DRAFT: 'draft',
  AVAILABLE: 'available',
  SOLD: 'sold',
  CLOSED: 'closed',
});

export const ListingState = Object.freeze({
  DRAFT: 'draft',
  PUBLISHED: 'published',
  CLOSED: 'closed',
  SOLD: 'sold',
});

export const CropperShape = Object.freeze({
  RECTANGLE: 'rect',
  ROUND: 'round',
});

export const ProductType = Object.freeze({
  NEW: 'sell-new-products',
  USED: 'sell-used-products',
});

export const ProductConditionKey = Object.freeze({
  NEW_TAGS: 'newtags',
  WITHOUT_TAGS: 'withouttags',
  VERY_GOOD: 'vgood',
  GOOD: 'good',
  SATISFACTORY: 'satisfactory',
});

export const SliderSettings = Object.freeze({
  dots: false,
  arrows: true,
  infinite: true,
  autoplay: false,
  autoplaySpeed: 2000,
  speed: 500,
  slidesToShow: 4,
  slidesToScroll: 1,
  variableWidth: false,
  focusOnSelect: false,
  centerMode: false,
  responsive: [
    {
      breakpoint: 1500,
      settings: {
        slidesToShow: 4,
      },
    },
    {
      breakpoint: 1300,
      settings: {
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 992,
      settings: {
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 768,
      settings: {
        centerMode: true,
        slidesToShow: 1,
      },
    },
    {
      breakpoint: 480,
      settings: {
        centerMode: true,
        slidesToShow: 1,
      },
    },
  ],
});

export const FieldConfigKey = Object.freeze({
  BRAND_NAME: 'brandname',
  SIZE: 'size',
  SOLE_TYPE: 'soletype',
  PRODUCT_CONDITION: 'productcondition',
  ADULT_KID: 'adkid',
});

// Filter priority order
export const FilterPriorityOrder = Object.freeze({
  size: 1,
  brandname: 2,
  productcondition: 3,
  soletype: 4,
  model: 5,
});

export const ImageVariants = Object.freeze({
  DEFAULT: 'default',
  LISTING_CARD: 'listing-card',
});

export const DeliveryMethod = Object.freeze({
  PICKUP: 'pickup',
  SHIPPING: 'shipping',
});

export const ShippingPreference = Object.freeze({
  PLATFORM: 'platform',
  OWN: 'own',
});

export const CheckoutSteps = Object.freeze({
  SHIPPING: 'shipping',
  CARRIERS: 'carriers',
  PAYMENT: 'payment',
});

export const Transitions = Object.freeze({
  ACCEPT: 'transition/accept',
  RETURN_PRODUCT: 'transition/return-product',
  MARK_DELIVERED: 'transition/mark-delivered',
  MARK_RECEIVED: 'transition/mark-received',
  RETURN_PRODUCT: 'transition/return-product',
  RECEIVED_RETURNED_PRODUCT: 'transition/received-returned-product',
  REQUEST_PAYMENT: 'transition/request-payment',
  CONFIRM_PAYMENT: 'transition/confirm-payment',
  EVIDENCE_EXCHANGE_BY_SELLER: 'transition/evidence-exchange-by-seller',
  EVIDENCE_EXCHANGE_BY_SELLER_AGAIN: 'transition/evidence-exchange-by-seller-again',
  EVIDENCE_EXCHANGE_BY_BUYER: 'transition/evidence-exchange-by-buyer',
  EVIDENCE_EXCHANGE_BY_BUYER_AGAIN: 'transition/evidence-exchange-by-buyer-again',
  FINAL_REVIEW_AND_REFUND: 'transition/final-review-and-refund',
  FINAL_REVIEW_AND_FUND_RELEASED: 'transition/final-review-and-fund-released',
});

export const UnifiedTransitions = Object.freeze({
  REQUEST_PAYMENT: [
    'transition/request-payment',
    'transition/request-payment-after-inquiry',
    'transition/request-payment-after-offer-accepted',
    'transition/request-payment-with-paypal',
    'transition/request-payment-after-enquiry-with-paypal',
    'transition/request-payment-with-wallet',
    'transition/request-payment-after-enquiry-with-wallet',
  ],

  CONFIRM_PAYMENT: [
    'transition/confirm-payment',
    'transition/confirm-payment-with-paypal',
    'transition/confirm-payment-with-wallet',
  ],

  EXPIRE_PAYMENT: [
    'transition/expire-payment',
    'transition/expire-payment-with-paypal',
    'transition/expire-payment-with-wallet',
  ],

  MARK_DELIVERED: [
    'transition/mark-delivered',
    'transition/operator-mark-delivered',
    'transition/mark-delivered-with-paypal',
    'transition/mark-delivered-with-paypal-operator',
    'transition/mark-delivered-with-wallet',
    'transition/mark-delivered-with-wallet-operator',
  ],

  MARK_RECEIVED: [
    'transition/mark-received',
    'transition/operator-mark-received',
    'transition/mark-received-from-purchased',
    'transition/mark-received-from-purchased-operator',
    'transition/mark-received-with-paypal',
    'transition/mark-received-with-paypal-operator',
    'transition/mark-received-with-wallet',
    'transition/mark-received-with-wallet-operator',
    'transition/mark-received-from-purchased-with-paypal',
    'transition/mark-received-from-purchased-with-paypal-operator',
    'transition/mark-received-from-purchased-with-wallet',
    'transition/mark-received-from-purchased-with-wallet-operator',
    'transition/mark-received-from-disputed',
    'transition/mark-received-from-disputed-with-paypal',
    'transition/mark-received-from-disputed-with-wallet',
  ],

  MARK_RECEIVED_FROM_DISPUTED: [
    'transition/mark-received-from-disputed',
    'transition/mark-received-from-disputed-with-paypal',
    'transition/mark-received-from-disputed-with-wallet',
  ],

  AUTO_MARK_RECEIVED: [
    'transition/auto-mark-received',
    'transition/auto-mark-received-with-paypal',
    'transition/auto-mark-received-with-wallet',
  ],

  CANCEL: [
    'transition/cancel',
    'transition/cancel-with-paypal',
    'transition/cancel-with-wallet',
    'transition/cancel-from-disputed',
    'transition/cancel-from-disputed-with-paypal',
    'transition/cancel-from-disputed-with-wallet',
  ],

  AUTO_CANCEL: [
    'transition/auto-cancel',
    'transition/auto-cancel-with-paypal',
    'transition/auto-cancel-with-wallet',
    'transition/auto-cancel-from-disputed',
    'transition/auto-cancel-from-disputed-with-paypal',
    'transition/auto-cancel-from-disputed-with-wallet',
  ],

  DISPUTE: [
    'transition/dispute',
    'transition/dispute-with-paypal',
    'transition/dispute-with-wallet',
    'transition/operator-dispute',
    'transition/operator-dispute-with-paypal',
    'transition/operator-dispute-with-wallet',
  ],

  // ✅ Evidence exchange loop — now includes wallet/paypal variants too
  EVIDENCE_EXCHANGE: [
    'transition/evidence-exchange-by-seller',
    'transition/evidence-exchange-by-seller-again',
    'transition/evidence-exchange-by-buyer',
    'transition/evidence-exchange-by-buyer-again',

    'transition/evidence-exchange-by-seller-with-paypal',
    'transition/evidence-exchange-by-seller-again-with-paypal',
    'transition/evidence-exchange-by-buyer-again-with-paypal',
    'transition/evidence-exchange-by-buyer-with-paypal',

    'transition/evidence-exchange-by-seller-with-wallet',
    'transition/evidence-exchange-by-seller-again-with-wallet',
    'transition/evidence-exchange-by-buyer-again-with-wallet',
    'transition/evidence-exchange-by-buyer-with-wallet',
  ],

  // ✅ Escalate — add method variants
  ESCALATE: [
    'transition/escalate-by-seller',
    'transition/escalate-by-buyer',
    'transition/escalate-by-seller-with-paypal',
    'transition/escalate-by-buyer-with-paypal',
    'transition/escalate-by-seller-with-wallet',
    'transition/escalate-by-buyer-with-wallet',
  ],

  // ✅ Operator: refund issued + return required — add variants
  REFUND_ISSUED: [
    'transition/refund-issued-return-required',
    'transition/refund-issued-return-required-with-paypal',
    'transition/refund-issued-return-required-with-wallet',
  ],

  // ✅ Provider confirms return received — add variants
  RETURN_RECEIVED: [
    'transition/return-received',
    'transition/return-received-with-paypal',
    'transition/return-received-with-wallet',
  ],

  // ✅ Provider reports issue with returned item — add variants
  RETURN_ISSUE: [
    'transition/problem-with-returned-item',
    'transition/problem-with-returned-item-with-paypal',
    'transition/problem-with-returned-item-with-wallet',
  ],

  // ✅ Escalation outcomes — add variants
  REFUND_WITHOUT_RETURN: [
    'transition/refund-without-return',
    'transition/refund-without-return-with-paypal',
    'transition/refund-without-return-with-wallet',
  ],

  FUNDS_RELEASED: [
    'transition/funds-released',
    'transition/funds-released-with-paypal',
    'transition/funds-released-with-wallet',
  ],

  // ✅ Final operator decisions — add variants
  FINAL_REVIEW_AND_REFUND: [
    'transition/final-review-and-refund',
    'transition/final-review-and-refund-with-paypal',
    'transition/final-review-and-refund-with-wallet',
  ],

  FINAL_REVIEW_AND_FUND_RELEASED: [
    'transition/final-review-and-fund-released',
    'transition/final-review-and-fund-released-with-paypal',
    'transition/final-review-and-fund-released-with-wallet',
  ],

  // (Optional) if you actually use these in wallet refund logic:
  REFUND_INITIATED: ['transition/refund-initiated', 'transition/refund-initiated-wallet'],

  // Keep your custom return-flow transitions (if used)
  RETURN_PRODUCT: ['transition/return-product'],
  RECEIVED_RETURNED_PRODUCT: ['transition/received-returned-product'],
});


export const BooleanEnum = Object.freeze({
  TRUE: true,
  FALSE: false,
});

export const OfferTransitions = Object.freeze({
  SEND_OFFER: 'transition/send-offer',
  EXPIRE_OFFER: 'transition/expire-offer',
  ACCEPT_OFFER: 'transition/accept-offer',
  DECLINE_OFFER: 'transition/decline-offer',
  COUNTER_OFFER: 'transition/counter-offer',
  ACCEPT_AFTER_COUNTER_1: 'transition/accept-after-counter-1',
  DECLINE_AFTER_COUNTER_1: 'transition/decline-after-counter-1',
  COUNTER_AFTER_COUNTER_1: 'transition/counter-after-counter-1',
  ACCEPT_AFTER_COUNTER_2: 'transition/accept-after-counter-2',
  DECLINE_AFTER_COUNTER_2: 'transition/decline-after-counter-2',
  COUNTER_AFTER_COUNTER_2: 'transition/counter-after-counter-2',
  ACCEPT_AFTER_COUNTER_3: 'transition/accept-after-counter-3',
  DECLINE_AFTER_COUNTER_3: 'transition/decline-after-counter-3',
  REQUEST_PAYMENT_AFTER_OFFER_ACCEPTED: 'transition/request-payment-after-offer-accepted',
});

export const OfferState = Object.freeze({
  INITIAL: 'initial',
  COUNTER: 'counter',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ALL: 'all',
});

export const DEFAULT_PACKAGE_DIMENSIONS = {
  height: '30',
  width: '20',
  length: '14',
  weight: '1.5',
  distanceUnit: { value: 'cm', label: 'Centimetres' },
  massUnit: { value: 'kg', label: 'Kilograms' },
};

export const ukSizes = [
  { optionName: 'UK 4', optionValue: 'uk4' },
  { optionName: 'UK 4.5', optionValue: 'uk45' },
  { optionName: 'UK 5', optionValue: 'uk5' },
  { optionName: 'UK 5.5', optionValue: 'uk55' },
  { optionName: 'UK 6', optionValue: 'uk6' },
  { optionName: 'UK 6.5', optionValue: 'uk65' },
  { optionName: 'UK 7', optionValue: 'uk7' },
  { optionName: 'UK 7.5', optionValue: 'uk75' },
  { optionName: 'UK 8', optionValue: 'uk8' },
  { optionName: 'UK 8.5', optionValue: 'uk85' },
  { optionName: 'UK 9', optionValue: 'uk9' },
  { optionName: 'UK 9.5', optionValue: 'uk95' },
  { optionName: 'UK 10', optionValue: 'uk10' },
  { optionName: 'UK 10.5', optionValue: 'uk105' },
  { optionName: 'UK 11', optionValue: 'uk11' },
  { optionName: 'UK 11.5', optionValue: 'uk115' },
  { optionName: 'UK 12', optionValue: 'uk12' },
  { optionName: 'UK 12.5', optionValue: 'uk125' },
  { optionName: 'UK 13', optionValue: 'uk13' },
  { optionName: 'UK 13.5', optionValue: 'uk135' },
  { optionName: 'UK 14', optionValue: 'uk14' },
];

export const PaymentMethodTypes = Object.freeze({
  STRIPE: 'stripe',
  PAYPAL: 'paypal',
  WALLET: 'wallet',
  APPLE_OR_GOOGLE_PAY: 'appleOrGooglePay',
});

export const TransactionProcessAlias = Object.freeze({
  DEFAULT_PURCHASE: 'default-purchase/release-1',
  DEFAULT_INQUIRY: 'default-inquiry/release-1',
  DEFAULT_BOOKING: 'default-booking/release-1',
});

export const StripeCardSelectionMode = Object.freeze({
  USE_EXISTING_DEFAULT: 'defaultCard',
  ADD_NEW_CARD: 'replaceCard',
});
